import React, { Suspense } from "react";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import TrailingSlashRedirect from "./components/public/TrailingSlashRedirect";
import RedirectToCanonicalSlash from "./components/public/RedirectToCanonicalSlash";
import RedirectArgomentiToBlog from "./components/public/RedirectArgomentiToBlog";
import { publicPath } from "./utils/canonicalUrl";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import { PazienteDocumentUploadProvider } from "./context/PazienteDocumentUploadContext";
import LoadingSpinner from "./components/public/LoadingSpinner";

// Auth Pages (sincroni per primo paint)
import LoginPage from "./pages/LoginPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import ScegliAreaPage from "./pages/app/ScegliAreaPage";

// Public Layout & Pages (sito pubblico separato dall'area autenticata)
import PublicLayout from "./layouts/PublicLayout";
import HomePage from "./pages/public/HomePage";
import MediciPage from "./pages/public/MediciPage";
import MedicoDetailPage from "./pages/public/MedicoDetailPage";
import RicercaPage from "./pages/public/RicercaPage";
import WidgetPrenotaPage from "./pages/public/WidgetPrenotaPage";
import ContattiPage from "./pages/public/ContattiPage";
import ChiSiamoPage from "./pages/public/ChiSiamoPage";
import PrivacyPage from "./pages/public/PrivacyPage";
import TerminiPage from "./pages/public/TerminiPage";
import CookiePolicyPage from "./pages/public/CookiePolicyPage";
import BlogPagePublic from "./pages/public/BlogPage";
import BlogPostPage from "./pages/public/BlogPostPage";
import DisciplinePage from "./pages/public/DisciplinePage";
import DisciplineDetailPage from "./pages/public/DisciplineDetailPage";
import FondiSanitariPage from "./pages/public/FondiSanitariPage";
import ComeFunzionaPage from "./pages/public/ComeFunzionaPage";
import PerLeStrutturePage from "./pages/public/PerLeStrutturePage";
import IntegrazioniPage from "./pages/public/IntegrazioniPage";
import StrutturePage from "./pages/public/StrutturePage";
import ServiziIndexPage from "./pages/public/ServiziIndexPage";
import ServizioSeoPage from "./pages/public/ServizioSeoPage";
import GonePage from "./pages/public/GonePage";
import NotFoundPage from "./pages/public/NotFoundPage";
import FeedbackPage from "./pages/public/FeedbackPage";
import UnsubscribePage from "./pages/public/UnsubscribePage";
import SeoResourceRedirect from "./components/public/SeoResourceRedirect";
import { getBookingIntent, clearBookingIntent } from "./utils/bookingIntent";

// Admin – lazy (area autenticata)
const AdminLayout = React.lazy(() => import("./layouts/AdminLayout"));
const StrutturaHubPage = React.lazy(() => import("./pages/app/struttura/StrutturaHubPage"));
const AdminDashboard = React.lazy(() => import("./pages/app/admin/Dashboard"));
const AdminMedici = React.lazy(() => import("./pages/app/admin/Medici"));
const AdminMedicoEditPage = React.lazy(() => import("./pages/app/admin/MedicoEditPage"));
const AdminMedicoDisponibilitaPage = React.lazy(() => import("./pages/app/admin/AdminMedicoDisponibilitaPage"));
const AdminPazienti = React.lazy(() => import("./pages/app/admin/Pazienti"));
const AdminAppuntamenti = React.lazy(() => import("./pages/app/admin/Appuntamenti"));
const AdminFatture = React.lazy(() => import("./pages/app/admin/Fatture"));
const AdminFatturaEditPage = React.lazy(() => import("./pages/app/admin/FatturaEditPage"));
const AdminSettingsLayout = React.lazy(() => import("./pages/app/admin/SettingsLayout"));
const AdminSettingsGenerali = React.lazy(() => import("./pages/app/admin/SettingsGenerali"));
const AdminSettingsPrestazioni = React.lazy(() => import("./pages/app/admin/SettingsPrestazioni"));
const AdminSettingsUtenti = React.lazy(() => import("./pages/app/admin/SettingsUtenti"));
const AdminBlog = React.lazy(() => import("./pages/app/admin/Blog"));
const AdminBlogEditPage = React.lazy(() => import("./pages/app/admin/BlogEditPage"));
const AdminMarketing = React.lazy(() => import("./pages/app/admin/Marketing"));
const AdminCrmPipeline = React.lazy(() => import("./pages/app/admin/CrmPipeline"));
const AdminCrmApprovals = React.lazy(() => import("./pages/app/admin/CrmApprovals"));

// Merchant – lazy
const MerchantDashboardPage = React.lazy(() => import("./pages/merchant/MerchantDashboardPage"));
const MerchantProductsPage = React.lazy(() => import("./pages/merchant/MerchantProductsPage"));
const MerchantOnboardingPage = React.lazy(() => import("./pages/merchant/MerchantOnboardingPage"));
const MerchantPaymentsPage = React.lazy(() => import("./pages/merchant/MerchantPaymentsPage"));
const MerchantSubscriptionsPage = React.lazy(() => import("./pages/merchant/MerchantSubscriptionsPage"));
const MerchantTerminalsPage = React.lazy(() => import("./pages/merchant/MerchantTerminalsPage"));
const MerchantReportsPage = React.lazy(() => import("./pages/merchant/MerchantReportsPage"));

// Medico – lazy
const MedicoLayout = React.lazy(() => import("./layouts/MedicoLayout"));
const MedicoDashboard = React.lazy(() => import("./pages/app/medico/Dashboard"));
const MedicoAgenda = React.lazy(() => import("./pages/app/medico/Agenda"));
const MedicoAppuntamenti = React.lazy(() => import("./pages/app/medico/Appuntamenti"));
const MedicoAppuntamentoDetailPage = React.lazy(() => import("./pages/app/medico/AppuntamentoDetailPage"));
const MedicoDisponibilita = React.lazy(() => import("./pages/app/medico/Disponibilita"));
const MedicoReferti = React.lazy(() => import("./pages/app/medico/Referti"));
const MedicoRefertoEditPage = React.lazy(() => import("./pages/app/medico/RefertoEditPage"));
const PazientiMedicoPage = React.lazy(() => import("./pages/app/medico/Pazienti"));
const StoriaClinicaPage = React.lazy(() => import("./pages/app/medico/StoriaClinicaPage"));

// Paziente – lazy
const PazienteLayout = React.lazy(() => import("./layouts/PazienteLayout"));
const PazienteDashboard = React.lazy(() => import("./pages/app/paziente/Dashboard"));
const PazienteAppuntamenti = React.lazy(() => import("./pages/app/paziente/Appuntamenti"));
const PazientePrenota = React.lazy(() => import("./pages/app/paziente/Prenota"));
const AppMedicoDetailPage = React.lazy(() => import("./pages/app/paziente/AppMedicoDetailPage"));
const PazienteReferti = React.lazy(() => import("./pages/app/paziente/Referti"));
const PazienteDocumentiSanitari = React.lazy(() => import("./pages/app/paziente/DocumentiSanitari"));
const PazienteFatture = React.lazy(() => import("./pages/app/paziente/Fatture"));
const PazienteProfilo = React.lazy(() => import("./pages/app/paziente/Profilo"));

// Redirect Component: 1 ruolo → dashboard; più ruoli → pagina Scegli area
const RedirectToDashboard = () => {
  const { isAuthenticated, getRoles } = useAuth();
  const location = useLocation();
  const rolesFromState = location.state?.roles;
  const roles = Array.isArray(rolesFromState) && rolesFromState.length > 0 ? rolesFromState : getRoles();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (roles.length === 0) {
    return <Navigate to="/login" replace />;
  }
  if (roles.length > 1) {
    return <Navigate to="/app/scegli-area" replace state={{}} />;
  }
  const role = roles[0];
  if (role === 'ADMIN') return <Navigate to="/admin/dashboard" replace />;
  if (role === 'STRUTTURA_ADMIN') return <Navigate to="/app/struttura" replace />;
  if (role === 'MEDICO') return <Navigate to="/app/medico/dashboard" replace />;
  if (role === 'PAZIENTE') {
    const intent = getBookingIntent();
    if (intent?.path) {
      clearBookingIntent();
      return <Navigate to={intent.path} replace state={intent.state || {}} />;
    }
    return <Navigate to="/app/paziente/dashboard" replace />;
  }
  if (role === 'MERCHANT') return <Navigate to="/merchant/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

// Root = sempre sito web (stesso URL/endpoint del sito; visibile anche se loggato)
const HomeOrRoot = () => (
  <PublicLayout>
    <HomePage />
  </PublicLayout>
);

// Entry point webapp: se non loggato -> login, se loggato -> dashboard per ruolo
const WebappEntry = () => <RedirectToDashboard />;

const RouteFallback = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-50 p-5">
    <LoadingSpinner message="Caricamento..." />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <PazienteDocumentUploadProvider>
      <RouteScrollToTop />
      <TrailingSlashRedirect />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
        <Route path="/register" element={<PublicLayout><RegisterPage /></PublicLayout>} />
        <Route path="/forgot-password" element={<PublicLayout><ForgotPasswordPage /></PublicLayout>} />
        <Route path="/reset-password" element={<PublicLayout><ResetPasswordPage /></PublicLayout>} />
        <Route path="/access-denied" element={<PublicLayout><AccessDeniedPage /></PublicLayout>} />

        {/* Sito pubblico */}
        <Route path="/" element={<HomeOrRoot />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/home/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/ricerca" element={<Navigate to={publicPath('/ricerca')} replace />} />
        <Route path="/ricerca/" element={<PublicLayout><RicercaPage /></PublicLayout>} />
        <Route path="/widget/:slug" element={<RedirectToCanonicalSlash />} />
        <Route path="/widget/:slug/" element={<WidgetPrenotaPage />} />
        <Route path="/medici" element={<Navigate to={publicPath('/medici')} replace />} />
        <Route path="/medici/" element={<PublicLayout><MediciPage /></PublicLayout>} />
        <Route path="/medici/:slug" element={<RedirectToCanonicalSlash />} />
        <Route path="/medici/:slug/" element={<PublicLayout><MedicoDetailPage /></PublicLayout>} />
        <Route path="/discipline" element={<Navigate to={publicPath('/discipline')} replace />} />
        <Route path="/discipline/" element={<PublicLayout><DisciplinePage /></PublicLayout>} />
        <Route path="/discipline/:slug" element={<RedirectToCanonicalSlash />} />
        <Route path="/discipline/:slug/" element={<PublicLayout><DisciplineDetailPage /></PublicLayout>} />
        <Route path="/contatti" element={<Navigate to={publicPath('/contatti')} replace />} />
        <Route path="/contatti/" element={<PublicLayout><ContattiPage /></PublicLayout>} />
        <Route path="/chi-siamo" element={<Navigate to={publicPath('/chi-siamo')} replace />} />
        <Route path="/chi-siamo/" element={<PublicLayout><ChiSiamoPage /></PublicLayout>} />
        <Route path="/fondi-sanitari" element={<Navigate to={publicPath('/fondi-sanitari')} replace />} />
        <Route path="/fondi-sanitari/" element={<PublicLayout><FondiSanitariPage /></PublicLayout>} />
        <Route path="/come-funziona" element={<Navigate to={publicPath('/come-funziona')} replace />} />
        <Route path="/come-funziona/" element={<PublicLayout><ComeFunzionaPage /></PublicLayout>} />
        <Route path="/per-le-strutture" element={<Navigate to={publicPath('/per-le-strutture')} replace />} />
        <Route path="/per-le-strutture/" element={<PublicLayout><PerLeStrutturePage /></PublicLayout>} />
        <Route path="/integrazioni" element={<Navigate to={publicPath('/integrazioni')} replace />} />
        <Route path="/integrazioni/" element={<PublicLayout><IntegrazioniPage /></PublicLayout>} />
        <Route path="/strutture" element={<Navigate to={publicPath('/strutture')} replace />} />
        <Route path="/strutture/" element={<PublicLayout><StrutturePage /></PublicLayout>} />
        <Route path="/servizi" element={<Navigate to={publicPath('/servizi')} replace />} />
        <Route path="/servizi/" element={<PublicLayout><ServiziIndexPage /></PublicLayout>} />
        <Route path="/servizi/:slug" element={<RedirectToCanonicalSlash />} />
        <Route path="/servizi/:slug/" element={<PublicLayout><ServizioSeoPage /></PublicLayout>} />
        <Route path="/privacy" element={<Navigate to={publicPath('/privacy')} replace />} />
        <Route path="/privacy/" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
        <Route path="/termini" element={<Navigate to={publicPath('/termini')} replace />} />
        <Route path="/termini/" element={<PublicLayout><TerminiPage /></PublicLayout>} />
        <Route path="/cookie-policy" element={<Navigate to={publicPath('/cookie-policy')} replace />} />
        <Route path="/cookie-policy/" element={<PublicLayout><CookiePolicyPage /></PublicLayout>} />
        <Route path="/feedback/:token" element={<FeedbackPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/blog" element={<Navigate to={publicPath('/blog')} replace />} />
        <Route path="/blog/" element={<PublicLayout><BlogPagePublic /></PublicLayout>} />
        <Route path="/blog/page/:pageNum" element={<RedirectToCanonicalSlash />} />
        <Route path="/blog/page/:pageNum/" element={<PublicLayout><BlogPagePublic /></PublicLayout>} />
        <Route path="/blog/page/" element={<Navigate to={publicPath('/blog')} replace />} />
        <Route path="/blog/:slug" element={<RedirectToCanonicalSlash />} />
        <Route path="/blog/:slug/" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
        <Route path="/argomenti" element={<RedirectArgomentiToBlog />} />
        <Route path="/argomenti/" element={<RedirectArgomentiToBlog />} />
        <Route path="/argomenti/:slug" element={<RedirectArgomentiToBlog />} />
        <Route path="/argomenti/:slug/" element={<RedirectArgomentiToBlog />} />
        <Route path="/404" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
        <Route path="/410" element={<PublicLayout><GonePage /></PublicLayout>} />
        <Route path="/sitemap.xml" element={<SeoResourceRedirect resource="sitemap.xml" />} />
        <Route path="/robots.txt" element={<SeoResourceRedirect resource="robots.txt" />} />

        {/* Entry point webapp: /app -> login se non loggato, altrimenti dashboard per ruolo o Scegli area */}
        <Route path="/app" element={<WebappEntry />} />
        <Route path="/app/scegli-area" element={
            <ProtectedRoute>
              <ScegliAreaPage />
            </ProtectedRoute>
          } />

        <Route
          path="/app/struttura"
          element={
            <ProtectedRoute requiredRoles={['ADMIN', 'STRUTTURA_ADMIN']}>
              <Suspense fallback={<LoadingSpinner />}>
                <StrutturaHubPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        
        {/* Admin Routes (Virtual Clinic) */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/medici"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminMedici />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/medici/:id"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminMedicoEditPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/medici/:idMedico/disponibilita"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminMedicoDisponibilitaPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/pazienti"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminPazienti />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/appuntamenti"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminAppuntamenti />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fatture"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminFatture />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fatture/nuovo"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminFatturaEditPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fatture/:id"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminFatturaEditPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminSettingsLayout />
              </AdminLayout>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="generali" replace />} />
          <Route path="generali" element={<AdminSettingsGenerali />} />
          <Route path="prestazioni" element={<AdminSettingsPrestazioni />} />
          <Route path="utenti" element={<AdminSettingsUtenti />} />
        </Route>
        <Route
          path="/admin/blog"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminBlog />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog/articoli/:id"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminBlogEditPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/marketing"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminMarketing />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/crm/pipeline"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminCrmPipeline />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/crm/approvals"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminLayout>
                <AdminCrmApprovals />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        {/* Merchant Routes */}
        <Route
          path="/merchant/dashboard"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <MerchantDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/onboarding"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <MerchantOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/products"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <MerchantProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/payments"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <MerchantPaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/subscriptions"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <MerchantSubscriptionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/terminals"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <MerchantTerminalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/reports"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <MerchantReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/profile"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <div>Merchant Profile - Coming Soon</div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant/settings"
          element={
            <ProtectedRoute requiredRole="MERCHANT">
              <div>Merchant Settings - Coming Soon</div>
            </ProtectedRoute>
          }
        />

        {/* Medico Routes (Virtual Clinic) - ADMIN può accedere all'area medico */}
        <Route
          path="/app/medico/dashboard"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <MedicoDashboard />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/agenda"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <Suspense fallback={<LoadingSpinner />}>
                  <MedicoAgenda />
                </Suspense>
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/appuntamenti"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <MedicoAppuntamenti />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/appuntamenti/:id"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <Suspense fallback={<LoadingSpinner />}>
                  <MedicoAppuntamentoDetailPage />
                </Suspense>
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/disponibilita"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <MedicoDisponibilita />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/referti"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <MedicoReferti />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/referti/nuovo"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <MedicoRefertoEditPage />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/referti/:id/modifica"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <MedicoRefertoEditPage />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/pazienti"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <PazientiMedicoPage />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/medico/pazienti/:id/storia-clinica"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "MEDICO"]}>
              <MedicoLayout>
                <StoriaClinicaPage />
              </MedicoLayout>
            </ProtectedRoute>
          }
        />

        {/* Paziente Routes (Virtual Clinic) */}
        <Route
          path="/app/paziente/dashboard"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazienteDashboard />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/appuntamenti"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazienteAppuntamenti />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/prenota"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazientePrenota />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/prenota/medico/:slug"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <AppMedicoDetailPage />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/referti"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazienteReferti />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/documenti"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazienteDocumentiSanitari />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/documenti/:idAppuntamento"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazienteDocumentiSanitari />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/fatture"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazienteFatture />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/paziente/profilo"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "PAZIENTE"]}>
              <PazienteLayout>
                <PazienteProfilo />
              </PazienteLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all: URL non riconosciute, tipicamente vecchie pagine indicizzate. */}
        <Route path="*" element={<PublicLayout><GonePage /></PublicLayout>} />
      </Routes>
      </Suspense>
      </PazienteDocumentUploadProvider>
    </BrowserRouter>
  );
}

export default App;
